import React from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { useIntakesSetHandlerCreateMutation } from '@/services/api/intakeService';
import {
  IntakeHandler,
  IntakeHandlerValue,
} from '@/services/types/intake-types';

interface HandlerToggleProps {
  intakeId: string;
  value?: IntakeHandlerValue;
  disabled?: boolean;
  className?: string;
}

/**
 * Toggle component to designate whether Finch or Firm handles follow-ups.
 * Cash App style — pill toggle with zinc palette.
 * Saves immediately on click via dedicated handler endpoint.
 */
export const HandlerToggle: React.FC<HandlerToggleProps> = ({
  intakeId,
  value = IntakeHandler.FINCH,
  disabled = false,
  className,
}) => {
  const [setHandler, { isLoading }] = useIntakesSetHandlerCreateMutation();

  const handleToggle = async (newHandler: IntakeHandlerValue) => {
    if (newHandler === value || isLoading || disabled) return;
    try {
      await setHandler({ id: intakeId, handler: newHandler }).unwrap();
    } catch {
      toast.error('Failed to update handler');
    }
  };

  const isFinch = value === IntakeHandler.FINCH;
  const isFirm = value === IntakeHandler.FIRM;
  const isDisabled = disabled || isLoading;

  return (
    <div
      className={cn(
        'inline-flex rounded-lg bg-zinc-100 p-0.5 gap-0.5',
        isDisabled && 'opacity-50 pointer-events-none',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => handleToggle(IntakeHandler.FINCH)}
        className={cn(
          'px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all',
          isFinch
            ? 'bg-zinc-900 text-white shadow-sm'
            : 'text-zinc-500 hover:text-zinc-700',
        )}
        disabled={isDisabled}
      >
        Finch
      </button>
      <button
        type="button"
        onClick={() => handleToggle(IntakeHandler.FIRM)}
        className={cn(
          'px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-all',
          isFirm
            ? 'bg-zinc-900 text-white shadow-sm'
            : 'text-zinc-500 hover:text-zinc-700',
        )}
        disabled={isDisabled}
      >
        Firm
      </button>
    </div>
  );
};

export default HandlerToggle;
